import React, { useContext, useMemo, useState } from "react";
import { VelavuAPI, VelavuMappedinIntegration } from "velavu-js-api";
import { useCancellableEffect } from "../../helper/hook-helper";

const MAPPEDIN_TOKEN_REFRESH_BUFFER_MS = 30_000;
const EMPTY_SITE_VENUE_MAP: ReadonlyMap<string, string> = new Map();

export interface MappedinIntegrationContextValue {
	integration: VelavuMappedinIntegration | null;
	accessToken: string | undefined;
	siteVenueMap: ReadonlyMap<string, string>;
	setMappedinIntegration(integration: VelavuMappedinIntegration | null): void;
}

export const MappedinIntegrationContext =
	React.createContext<MappedinIntegrationContextValue>({
		integration: null,
		accessToken: undefined,
		siteVenueMap: EMPTY_SITE_VENUE_MAP,
		setMappedinIntegration: () => undefined,
	});

export interface MappedinIntegrationProviderProps {
	children: React.ReactNode;
}

/**
 * Gets the delay before a Mappedin token should be refreshed.
 */
function getMappedinTokenRefreshDelay(tokenExpiry: string): number | undefined {
	const tokenExpiryMs = Date.parse(tokenExpiry);
	if (!Number.isFinite(tokenExpiryMs)) {
		return undefined;
	}

	return Math.max(
		0,
		tokenExpiryMs - Date.now() - MAPPEDIN_TOKEN_REFRESH_BUFFER_MS,
	);
}

/**
 * Checks if a Mappedin token is usable (has not yet reached its actual expiry).
 */
function isMappedinTokenUsable(tokenExpiry: string): boolean {
	const tokenExpiryMs = Date.parse(tokenExpiry);
	return Number.isFinite(tokenExpiryMs) && tokenExpiryMs > Date.now();
}

/**
 * Requests and maintains up-to-date Mappedin integration data.
 */
export function MappedinIntegrationProvider(
	props: MappedinIntegrationProviderProps,
) {
	const [integration, setIntegration] =
		useState<VelavuMappedinIntegration | null>(null);

	//Load the initial integration state
	useCancellableEffect((addPromise) => {
		addPromise(VelavuAPI.integrations.getIntegrations())
			.then((integrations) =>
				setIntegration(integrations.mappedin ?? null),
			)
			.catch((error) => {
				console.warn("Failed to load integrations", error);
				setIntegration(null);
			});
	}, []);

	//Refresh the token if it is close to expiring
	const tokenExpiry = integration?.token_expiry;
	useCancellableEffect(
		(addPromise) => {
			if (tokenExpiry === undefined) {
				return;
			}

			const refreshDelay = getMappedinTokenRefreshDelay(tokenExpiry);
			if (refreshDelay === undefined) {
				return;
			}

			const refreshTimer = setTimeout(() => {
				addPromise(
					VelavuAPI.integrations
						.getIntegrations()
						.then((integrations) =>
							setIntegration(integrations.mappedin ?? null),
						)
						.catch((error) => {
							console.warn(
								"Failed to refresh Mappedin token",
								error,
							);
							setIntegration(null);
						}),
				);
			}, refreshDelay);

			return () => {
				clearTimeout(refreshTimer);
			};
		},
		[tokenExpiry],
	);

	//Get the access token for the Mappedin integration, if it is valid
	const accessToken = useMemo(() => {
		if (
			integration === null ||
			!isMappedinTokenUsable(integration.token_expiry)
		) {
			return undefined;
		}

		return integration.token;
	}, [integration]);

	//Create a mapping of Velavu site IDs to Mappedin venue IDs
	const siteVenueMap = useMemo((): ReadonlyMap<string, string> => {
		if (integration === null) {
			return EMPTY_SITE_VENUE_MAP;
		}

		const result = new Map<string, string>();
		for (const importItem of integration.imports) {
			result.set(importItem.site_id, importItem.venue_id);
		}
		return result;
	}, [integration]);

	const providerValue = useMemo(
		(): MappedinIntegrationContextValue => ({
			integration,
			accessToken,
			siteVenueMap,
			setMappedinIntegration: setIntegration,
		}),
		[integration, accessToken, siteVenueMap, setIntegration],
	);

	return (
		<MappedinIntegrationContext.Provider value={providerValue}>
			{props.children}
		</MappedinIntegrationContext.Provider>
	);
}

export function useMappedinIntegrationContext(): MappedinIntegrationContextValue {
	return useContext(MappedinIntegrationContext);
}
